import React from "react";
import DataList from "components/datatable/DataList";
import useAxios from "hooks/useAxios";
import useAxiosFunction from "hooks/useAxiosFunction";
import useMsgBox from "hooks/useMsgBox";
import { useAuth } from "context/AuthProvider";
import { RuleRequired } from "utils/MyUtils";
import { boolText, hasPermission, parseBoolean, showEmpty } from "features/kms/system/SystemPageUtils";

const RulePositive = {
  required: "Wajib diisi",
  min: { value: 1, message: "Wajib diisi" },
};

export default function CaborManagementPage() {
  const { user } = useAuth();
  const [, , , axiosFetch] = useAxiosFunction();
  const [showMsg, confirmMsg] = useMsgBox();
  const [reloadKey, setReloadKey] = React.useState(0);
  const [kategoriOlimpiadeResp] = useAxios({ method: "GET", url: "/references/kategori-olimpiade", auth: true });
  const [kategoriPrestasiResp] = useAxios({ method: "GET", url: "/references/kategori-prestasi", auth: true });
  const groupName = String(user?.groupname || "").toUpperCase();
  const canManage = hasPermission(user, "reference.manage") && ["SUPERADMIN", "ADMIN_KONI_PROVINSI", "ADMIN_KONI_KABKOTA"].includes(groupName);
  const canDelete = canManage && !["ADMIN_CABOR", "ADMIN_KLUB"].includes(groupName);
  const kategoriOlimpiadeOptions = Array.isArray(kategoriOlimpiadeResp?.data) ? kategoriOlimpiadeResp.data : [];
  const kategoriPrestasiOptions = Array.isArray(kategoriPrestasiResp?.data) ? kategoriPrestasiResp.data : [];
  const statusAktifOptions = [
    { id: "true", nama: "Aktif" },
    { id: "false", nama: "Nonaktif" },
  ];
  const canManageRow = (item) => parseBoolean(item?.can_manage_row);
  const canMarkOfficial = (item) => parseBoolean(item?.can_mark_official);
  const listColumns = [
    "no",
    "nama",
    "kategori_olimpiade_name",
    "kategori_prestasi_name",
    "usia_puncak_range",
    "aktif",
    ...(canManage ? ["action"] : []),
  ];

  const coltype = {
    id_cabor: { type: "hidden", name: "id_cabor" },
    nama: {
      label: "Nama Cabor",
      type: "text",
      name: "nama",
      colsize: 12,
      validation: RuleRequired,
      width: 240,
    },
    id_kategori_olimpiade: {
      label: "Kategori Olimpiade",
      type: "dropdown",
      name: "id_kategori_olimpiade",
      colsize: 6,
      validation: RuleRequired,
      options: kategoriOlimpiadeOptions,
      valueId: "id_kategori_olimpiade",
      labelId: "nama",
      placeholder: "-Pilih Kategori Olimpiade-",
      width: 160,
    },
    kategori_olimpiade_name: {
      label: "Kategori Olimpiade",
      renderFunc: (val) => showEmpty(val),
      width: 160,
    },
    id_kategori_prestasi: {
      label: "Kategori Prestasi",
      type: "dropdown",
      name: "id_kategori_prestasi",
      colsize: 6,
      validation: RuleRequired,
      options: kategoriPrestasiOptions,
      valueId: "id_kategori_prestasi",
      labelId: "nama",
      placeholder: "-Pilih Kategori Prestasi-",
      width: 150,
    },
    kategori_prestasi_name: {
      label: "Kategori Prestasi",
      renderFunc: (val) => showEmpty(val),
      width: 150,
    },
    awal_usia_puncak: {
      label: "Awal Usia Puncak",
      type: "number",
      name: "awal_usia_puncak",
      colsize: 6,
      validation: RulePositive,
      min: 1,
      width: 130,
      align: "center",
    },
    akhir_usia_puncak: {
      label: "Akhir Usia Puncak",
      type: "number",
      name: "akhir_usia_puncak",
      colsize: 6,
      validation: RulePositive,
      min: 1,
      width: 130,
      align: "center",
    },
    usia_puncak_range: {
      label: "Usia Puncak",
      renderFunc: (val, col, item) => {
        const awal = showEmpty(item?.awal_usia_puncak);
        const akhir = showEmpty(item?.akhir_usia_puncak);
        if (awal === "-" || akhir === "-") return showEmpty(val);
        return `${awal}-${akhir}`;
      },
      renderExportFunc: (val, col, item) => {
        const awal = item?.awal_usia_puncak ?? "";
        const akhir = item?.akhir_usia_puncak ?? "";
        return awal !== "" && akhir !== "" ? `${awal}-${akhir}` : showEmpty(val);
      },
      width: 120,
      align: "center",
    },
    aktif: {
      label: "Status",
      type: "checkbox",
      name: "aktif",
      renderFunc: (val) => boolText(val),
      colsize: 3,
      width: 100,
      align: "center",
    },
    action: { label: "Aksi", align: "center", width: 100 },
  };

  const saveHandle = async (form, defaultValue = {}) => {
    const id = form?.id_cabor || defaultValue?.id_cabor || "";
    const method = id ? "PUT" : "POST";
    const url = id ? `/master/cabor/${id}` : "/master/cabor";

    return axiosFetch({
      method,
      url,
      auth: true,
      data: {
        nama: form?.nama || "",
        id_kategori_olimpiade: Number(form?.id_kategori_olimpiade || 0),
        id_kategori_prestasi: Number(form?.id_kategori_prestasi || 0),
        awal_usia_puncak: Number(form?.awal_usia_puncak || 0),
        akhir_usia_puncak: Number(form?.akhir_usia_puncak || 0),
      },
    });
  };

  const markOfficialHandle = async (item) => {
    const ok = await confirmMsg(
      "Konfirmasi",
      `Apakah Anda yakin akan meresmikan data ${item?.nama || "ini"}?`,
    );
    if (!ok) return;

    const result = await axiosFetch({
      method: "GET",
      url: `/master/cabor/${item?.id_cabor}/mark-official`,
      auth: true,
    });
    if (result?.data?.success === true) {
      await showMsg("Informasi", "Data berhasil diresmikan.");
      setReloadKey((current) => current + 1);
    } else {
      await showMsg("Error", result?.message || "Gagal meresmikan data.");
    }
  };

  const renderActionExtension = (val, col, item) => {
    if (!canMarkOfficial(item)) {
      return null;
    }
    return (
      <>
        <a
          href="#"
          onClick={(event) => {
            event.preventDefault();
            markOfficialHandle(item);
          }}
          className="btn btn-success btn-xs"
          title="Resmikan"
        >
          <i className="fa fa-certificate"></i>
        </a>
        &nbsp;
      </>
    );
  };

  return (
    <>
      <DataList
        title="Cabang Olahraga"
        coltype={coltype}
        dlist={{
          url: "/master/cabor",
          columns: listColumns,
          filter: [
            { dtype: "q", name: "q", colsize: 3 },
            {
              dtype: "dropdown",
              name: "id_kategori_olimpiade",
              label: "Kategori Olimpiade",
              colsize: 3,
              options: kategoriOlimpiadeOptions,
              valueId: "id_kategori_olimpiade",
              labelId: "nama",
              placeholder: "-Semua Kategori Olimpiade-",
            },
            {
              dtype: "dropdown",
              name: "id_kategori_prestasi",
              label: "Kategori Prestasi",
              colsize: 3,
              options: kategoriPrestasiOptions,
              valueId: "id_kategori_prestasi",
              labelId: "nama",
              placeholder: "-Semua Kategori Prestasi-",
            },
            {
              dtype: "dropdown",
              name: "aktif",
              label: "Status",
              colsize: 3,
              options: statusAktifOptions,
              valueId: "id",
              labelId: "nama",
              placeholder: "-Semua Status-",
            },
          ],
          sort_by: ["nama", "kategori_olimpiade_name", "kategori_prestasi_name", "usia_puncak_range"],
          render_action_extension: renderActionExtension,
        }}
        reload={reloadKey}
        addconfig={canManage ? {
          columns: ["nama", "id_kategori_olimpiade", "id_kategori_prestasi", "awal_usia_puncak", "akhir_usia_puncak"],
          title: "Tambah Cabang Olahraga",
          label_tool: "Tambah Cabang Olahraga",
          defaultValue: {},
          saveHandle,
        } : null}
        editconfig={canManage ? {
          columns: ["id_cabor", "nama", "id_kategori_olimpiade", "id_kategori_prestasi", "awal_usia_puncak", "akhir_usia_puncak"],
          title: "Ubah Cabang Olahraga",
          url: "/master/cabor",
          urlitem: "/master/cabor",
          cellid: "id_cabor",
          isAllow: canManageRow,
          saveHandle,
          normalizeItem: (item) => ({ ...item }),
        } : null}
        delconfig={canDelete ? {
          url: "/master/cabor",
          cellid: "id_cabor",
          cellname: "nama",
          isAllow: canManageRow,
        } : null}
        activateconfig={canManage ? {
          url: "/master/cabor",
          cellid: "id_cabor",
          cellname: "nama",
          cellactivate: "aktif",
          isAllow: canManageRow,
          labelactivate: "Aktifkan",
          labelinactivate: "Non-aktifkan",
          cfmactivate: "mengaktifkan",
          cfminactivate: "menonaktifkan",
          infoactivate: "diaktifkan",
          infoinactivate: "dinonaktifkan",
        } : null}
      />
    </>
  );
}
